// Sign in, create account, reset password, and first-time studio setup.
import { signIn, signUp, sendReset, setPassword } from '../supa';
import { createStudio } from '../data';
import { html, $, toast } from '../ui';

type Mode = 'signin' | 'signup' | 'reset' | 'newpass';

export function authView(root: HTMLElement, mode: Mode = 'signin', note = ''): void {
  const copy = {
    signin: { h: 'Sign in', btn: 'Sign in' },
    signup: { h: 'Create your studio account', btn: 'Create account' },
    reset: { h: 'Reset your password', btn: 'Email me a reset link' },
    newpass: { h: 'Choose a new password', btn: 'Save password' },
  }[mode];
  root.innerHTML = html`<div class="auth"><div class="auth-card">
    <img class="logo" src="/archviewvr-logo.svg" alt="ArchviewVR">
    <h1>${copy.h}</h1>
    ${note ? html`<div class="notice">${note}</div>` : ''}
    <form id="auth-form" novalidate>
      ${mode === 'signup' ? html`<div class="field"><label for="a-name">Your name</label><input id="a-name" autocomplete="name" required></div>` : ''}
      ${mode !== 'newpass' ? html`<div class="field"><label for="a-email">Email</label><input id="a-email" type="email" autocomplete="email" required></div>` : ''}
      ${mode !== 'reset' ? html`<div class="field"><label for="a-pass">Password</label><input id="a-pass" type="password" autocomplete="${mode === 'signin' ? 'current-password' : 'new-password'}" minlength="8" required></div>` : ''}
      <div class="err" id="a-err" hidden></div>
      <button class="btn primary" type="submit" id="a-go">${copy.btn}</button>
    </form>
    ${mode === 'signin' ? html`<div class="alt"><a href="#/reset">Forgot your password?</a></div><div class="alt">New to ArchviewVR? <a href="#/signup">Create an account</a></div>` : ''}
    ${mode === 'signup' ? html`<div class="alt">Already have an account? <a href="#/signin">Sign in</a></div>` : ''}
    ${mode === 'reset' ? html`<div class="alt"><a href="#/signin">Back to sign in</a></div>` : ''}
  </div></div>`.html;

  const form = $<HTMLFormElement>('#auth-form', root);
  const err = $('#a-err', root);
  const go = $<HTMLButtonElement>('#a-go', root);
  const val = (id: string) => ($<HTMLInputElement>(id, root)?.value ?? '').trim();
  form.onsubmit = async (e) => {
    e.preventDefault();
    err.hidden = true;
    const email = val('#a-email'), pass = $<HTMLInputElement>('#a-pass', root)?.value ?? '';
    if (mode !== 'newpass' && !/^\S+@\S+\.\S+$/.test(email)) { err.textContent = 'Enter a valid email address.'; err.hidden = false; return; }
    if (mode !== 'reset' && pass.length < 8) { err.textContent = 'Use a password of at least 8 characters.'; err.hidden = false; return; }
    go.disabled = true;
    try {
      if (mode === 'signin') { await signIn(email, pass); location.hash = '#/'; }
      else if (mode === 'signup') {
        const mustConfirm = await signUp(email, pass, val('#a-name'));
        if (mustConfirm) authView(root, 'signin', `We sent a confirmation link to ${email}. Open it, then sign in here.`);
        else location.hash = '#/';
      } else if (mode === 'reset') {
        await sendReset(email);
        authView(root, 'signin', `If ${email} has an account, a reset link is on its way.`);
      } else {
        await setPassword(pass);
        toast('Password saved');
        location.hash = '#/';
      }
    } catch (ex) {
      err.textContent = ex instanceof Error ? ex.message : 'Something went wrong. Try again.';
      err.hidden = false;
      go.disabled = false;
    }
  };
}

/** First sign-in: name the studio. */
export function studioSetupView(root: HTMLElement, done: () => void): void {
  root.innerHTML = html`<div class="auth"><div class="auth-card">
    <img class="logo" src="/archviewvr-logo.svg" alt="ArchviewVR">
    <h1>Name your studio</h1>
    <p class="muted">This is the name your team and clients see. You can change it later.</p>
    <form id="studio-form" novalidate>
      <div class="field"><label for="s-name">Studio name</label><input id="s-name" placeholder="Enter studio name" required></div>
      <div class="err" id="s-err" hidden></div>
      <button class="btn primary" type="submit" id="s-go">Continue</button>
    </form>
  </div></div>`.html;
  const form = $<HTMLFormElement>('#studio-form', root);
  form.onsubmit = async (e) => {
    e.preventDefault();
    const name = $<HTMLInputElement>('#s-name', root).value.trim();
    const err = $('#s-err', root);
    if (name.length < 2) { err.textContent = 'Enter your studio’s name.'; err.hidden = false; return; }
    $<HTMLButtonElement>('#s-go', root).disabled = true;
    try { await createStudio(name); done(); }
    catch (ex) {
      err.textContent = ex instanceof Error ? ex.message : 'Could not create the studio.';
      err.hidden = false;
      $<HTMLButtonElement>('#s-go', root).disabled = false;
    }
  };
}
